<template>
  <tr class="item">
    <td class="sticky-col student-info-col">
      <a-row :align="'middle'" class="student-info">
        <a-col class="avatar">
          <div class="avatar__wrapper">
            <template v-if="student.avatar">
              <img :src="getAvatarUrl(student.avatar)" alt="" />
            </template>
            <template v-else>
              <img class="skeleton" src="@/assets/icons/camera.svg" alt="" />
            </template>
          </div>
        </a-col>
        <a-col>
          <a-row>
            <span class="student-name">{{ student.lastName }}</span>
          </a-row>
          <a-row>
            <span class="student-name">{{ student.firstName }}</span>
          </a-row>
        </a-col>
      </a-row>
    </td>

    <td
      v-for="(quality, index) in projectQualities"
      :key="quality.id"
      :class="`${
        index === projectQualities.length - 1
          ? 'quality-col-last'
          : 'quality-col'
      }`"
    >
      <a-spin :spinning="isLoadingQuality[index]">
        <c-checkbox
          class="quality-checkbox"
          :checked="getIsChecked(quality.id)"
          :disabled="disabled"
          @change="handleConfirm(index)"
        />
      </a-spin>
    </td>
  </tr>
</template>

<script setup>
  import { onMounted, ref } from 'vue';
  import CCheckbox from '@/components/UI/CCheckbox.vue';
  import { useApi } from '@/api/use-api';

  const props = defineProps({
    student: Object,
    projectQualities: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  const api = useApi();
  const executorQualities = ref([]);
  const isLoadingQuality = ref([]);

  const getAvatarUrl = (avatar) =>
    avatar ? `/api/v1/attachment/${avatar}` : null;

  onMounted(() => {
    executorQualities.value = props.student.qualities;
    isLoadingQuality.value = new Array(props.projectQualities.length).fill(
      false
    );
  });

  const getIsChecked = (qualityId) => {
    return executorQualities.value.some(
      (el) => el.qualityId === qualityId && el.isConfirmed
    );
  };

  const handleConfirm = async (index) => {
    isLoadingQuality.value[index] = true;

    const projectQuality = props.projectQualities[index];
    const executorQuality = executorQualities.value.find(
      (el) => el.qualityId === projectQuality.id
    );

    try {
      if (executorQuality) {
        const confirmed = !executorQuality.isConfirmed;
        await api.projectExecutorQuality.confirm(executorQuality.id, {
          isConfirmed: confirmed ? 1 : 0,
        });
        executorQuality.isConfirmed = !executorQuality.isConfirmed;
      } else {
        const response = await api.projectExecutorQuality.create({
          projectExecutorId: props.student.id,
          qualityId: projectQuality.id,
          confirmedAt: Math.floor(new Date().getTime() / 1000),
        });
        executorQualities.value.push({
          id: response.model.id,
          qualityId: projectQuality.id,
          qualityName: projectQuality.name,
          isConfirmed: true,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      isLoadingQuality.value[index] = false;
    }
  };
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;
  $color-text-secondary-grey-216: #718096;

  .item {
    background: $color-bg-secondary-grey;
    font: 400 14px/20px Nunito Sans;
    height: 88px;
    width: 100%;

    .student-info-col {
      .student-info {
        padding-right: 40px;
        min-width: 179px;
        flex-wrap: nowrap;
      }

      .avatar {
        margin: 20px;

        &__wrapper {
          border-radius: 50%;
          background-color: $color-bg-primary-white;
          overflow: hidden;
          position: relative;
          height: 50px;
          width: 50px;
          border: 2px solid #ffffff;

          img {
            margin: auto;
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            width: 100%;
          }

          .skeleton {
            width: auto;
          }
        }
      }
    }

    .quality-col {
      margin: 0 20px;
      text-align: center;
    }

    .quality-col-last {
      margin: 0 20px;
      text-align: center;
    }

    .quality-checkbox {
      border-spacing: 0 0;
    }

    .sticky-col {
      position: sticky;
      left: 0;
      z-index: 1;
      background: $color-bg-secondary-grey;
    }
  }
</style>
